import Image from 'next/image';
import Trans from 'next-translate/Trans';
import { useEffect, useState } from 'react';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import { CheckGlyph } from '@/components/agents/primitives';
import { type Logo, LOGOS } from '@/components/b2b/TeamsLogos';

import { useAuthT } from './kit';
import NeuralField from './NeuralField';

import LalamoveLogo from '~/svg/Lalamove.svg';
import BniLogo from '~/svg/Stripe.svg'; // BNI (the file name is historical)

// Two customers the b2b list doesn't show (the agents page has both), sized
// from each file's aspect ratio so five fit on one line at 1440 px.
const PANEL_ONLY_LOGOS: Logo[] = [
  { name: 'BNI', Svg: BniLogo, className: 'h-[22px] w-[57px]' },
  { name: 'Lalamove', Svg: LalamoveLogo, className: 'h-7 w-[100px]' },
];
const PANEL_LOGOS = [
  'Deloitte',
  'BNI',
  'Honeywell',
  'Lalamove',
  'UC San Diego',
];
const logos = PANEL_LOGOS.flatMap((name) => {
  const logo = [...LOGOS, ...PANEL_ONLY_LOGOS].find(
    (item) => item.name === name,
  );
  return logo ? [logo] : [];
});

// Emerald light from the top corner and teal from the bottom, behind the
// field; also the first paint, before the field starts.
const PANEL_LIGHT = [
  'radial-gradient(110% 75% at 80% 6%, rgba(16, 185, 129, 0.30) 0%, rgba(6, 78, 59, 0.20) 40%, transparent 72%)',
  'radial-gradient(85% 60% at 0% 100%, rgba(13, 148, 136, 0.22) 0%, transparent 70%)',
].join(', ');
// Film grain, so the gradients don't band.
const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";
// Darkens the bottom for the copy; the top stays the field's.
const SCRIM =
  'linear-gradient(180deg, rgba(2, 13, 10, 0.1) 0%, rgba(2, 13, 10, 0) 18%, rgba(2, 13, 10, 0) 40%, rgba(2, 13, 10, 0.72) 64%, rgba(2, 13, 10, 0.94) 100%)';

/**
 * The field runs only where the panel shows (lg+): phones never mount it.
 * Visitors who asked for reduced motion get a still frame.
 */
function useFieldMotion() {
  const [motion, setMotion] = useState({ show: false, animate: false });
  useEffect(() => {
    const wide = window.matchMedia('(min-width: 1024px)');
    const calm = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () =>
      setMotion({ show: wide.matches, animate: !calm.matches });
    update();
    wide.addEventListener('change', update);
    calm.addEventListener('change', update);
    return () => {
      wide.removeEventListener('change', update);
      calm.removeEventListener('change', update);
    };
  }, []);
  return motion;
}

export default function BrandPanel() {
  const { t } = useAuthT();
  const { show, animate } = useFieldMotion();

  return (
    <aside
      aria-label={t('About Easy-Peasy.AI')}
      className='hidden lg:sticky lg:top-0 lg:block lg:h-[100dvh] lg:p-3 xl:p-4'
    >
      <div className='relative isolate flex h-full flex-col justify-end overflow-hidden rounded-[28px] bg-[#03120e] ring-1 ring-inset ring-white/10 xl:rounded-[32px]'>
        <div
          aria-hidden='true'
          className='absolute inset-0 -z-30'
          style={{ backgroundImage: PANEL_LIGHT }}
        />
        {show && <NeuralField animate={animate} className='-z-20' />}
        <div
          aria-hidden='true'
          className='absolute inset-0 -z-10 opacity-[0.07]'
          style={{ backgroundImage: GRAIN }}
        />
        <div
          aria-hidden='true'
          className='absolute inset-0 -z-10'
          style={{ backgroundImage: SCRIM }}
        />

        <div className='px-8 pb-8 xl:px-12 xl:pb-11'>
          <p
            className={clsxm(
              styles.display,
              'max-w-[34rem] text-[2.5rem] font-semibold leading-[1.04] text-white xl:text-[3.25rem] [@media(max-height:700px)]:text-[2.125rem]',
            )}
          >
            {/* The second sentence gets its own line in the field's green;
                the padding keeps the gradient from clipping descenders. */}
            <Trans
              i18nKey='One AI platform. <0>Every task.</0>'
              ns='login'
              defaultTrans='One AI platform. <0>Every task.</0>'
              components={[
                <span
                  key='task'
                  className='-my-[0.1em] block w-fit bg-gradient-to-r from-emerald-200 to-teal-300 bg-clip-text py-[0.1em] text-transparent'
                />,
              ]}
            />
          </p>
          <p className='mt-4 max-w-[30rem] text-[16px] leading-7 text-white/[0.78] xl:text-[17px]'>
            {t(
              'Images, video, voice and Marky, the AI agent that does the work. All in one account, free to start.',
            )}
          </p>
          <ul className='mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[13px] font-medium text-white/80'>
            {[t('Free plan included'), t('No credit card required')].map(
              (item) => (
                <li key={item} className='flex items-center gap-1.5'>
                  <CheckGlyph className='h-4 w-4 text-emerald-300' />
                  {item}
                </li>
              ),
            )}
          </ul>

          <div className='mt-9 border-t border-white/10 pt-7 [@media(max-height:760px)]:hidden'>
            <p className='text-[13px] font-medium text-white/55'>
              {t('Trusted by teams at companies and universities like')}
            </p>
            <ul className='mt-5 flex flex-wrap items-center gap-x-8 gap-y-5'>
              {logos.map((logo) => (
                <li key={logo.name} className='flex h-8 items-center'>
                  {'Svg' in logo ? (
                    <logo.Svg
                      role='img'
                      aria-label={logo.name}
                      className={clsxm(
                        'flex-none opacity-75 brightness-0 invert',
                        logo.className,
                      )}
                    />
                  ) : (
                    <Image
                      src={logo.src}
                      alt={logo.name}
                      width={logo.width}
                      height={logo.height}
                      unoptimized={logo.src.endsWith('.svg')}
                      className={clsxm(
                        'flex-none opacity-75 brightness-0 invert',
                        logo.className,
                      )}
                    />
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </aside>
  );
}
